/****
 * Language switcher component that allows users to change their
 * application language preference, persist the selection, and
 * provide an accessible keyboard-navigable language menu.
 */
import { Globe01 } from "@untitledui/icons";
import React from "react";
import { useTranslation } from "react-i18next";

import { saveUserLanguage } from "@/services/language";
import { useAppSelector } from "@/store/hooks";
import { selectLanguage } from "@/store/userSlice";

type LangOption = {
  code: string;
  label: string;
};

const LANGUAGES: LangOption[] = [
  { code: "en", label: "English" },
  { code: "fr", label: "Français" },
];

const CODE_TO_LABEL: Record<string, string> = {
  en: "English",
  fr: "Français",
};
const getCodeFromLabel = (label?: string) => {
  if (!label) return "en";

  const normalized = label.toLowerCase();

  if (normalized.includes("fr")) return "fr";
  if (normalized.includes("en")) return "en";

  return "en";
};

// Language selector component with optional external control support
const LanguageSwitcher: React.FC<{
  upn: string;
  onOpenChange?: (open: boolean) => void;
  hideTrigger?: boolean;
  forceOpen?: boolean;
}> = ({
  upn,
  onOpenChange,
  hideTrigger = false,
  forceOpen = false,
}) => {
    const { i18n } = useTranslation();

    const storedLanguage = useAppSelector(selectLanguage);

    const [internalOpen, setInternalOpen] = React.useState(false);

    const open = forceOpen || internalOpen;
    const [activeIndex, setActiveIndex] = React.useState<number>(0);
    const [saving, setSaving] = React.useState(false);

    const wrapperRef = React.useRef<HTMLDivElement>(null);
    const buttonRef = React.useRef<HTMLButtonElement>(null);
    const listRef = React.useRef<HTMLDivElement>(null);
    const firstItemRef = React.useRef<HTMLButtonElement | null>(null);

    React.useEffect(() => {
      if (open) {
        firstItemRef.current?.focus();
      }
    }, [open]);


    React.useEffect(() => {
      if (!storedLanguage) return;

      const langCode = getCodeFromLabel(storedLanguage);

      if (i18n.language !== langCode) {
        i18n.changeLanguage(langCode);
      }
    }, [storedLanguage]);

    const [currentLang, setCurrentLang] = React.useState("en");

    React.useEffect(() => {
      const lang = (i18n.resolvedLanguage || i18n.language || "en").split("-")[0];
      setCurrentLang(lang);
    }, [i18n.resolvedLanguage, i18n.language]);

    // Change + Save (no button)
    const changeLanguage = async (langCode: string) => {
      if (saving || langCode === currentLang) return;

      try {
        setSaving(true);

        // UI update immediately
        i18n.changeLanguage(langCode);

        // Convert to full name for DB
        const languageLabel = CODE_TO_LABEL[langCode] || "English";

        await saveUserLanguage({
          upn,
          language: languageLabel, // FIX
        });
      } catch (err) {
        console.error("Failed to save language", err);
        i18n.changeLanguage(currentLang); // rollback
      } finally {
        setTimeout(() => setSaving(false), 800);
      }
      setInternalOpen(false);
      onOpenChange?.(false);
    };

    // Close on outside click
    React.useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
          setInternalOpen(false);
          onOpenChange?.(false);
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const onMenuKeyDown = (e: React.KeyboardEvent) => {
      if (!open) return;

      if (e.key === "Escape") {
        e.preventDefault();
        setInternalOpen(false);
        onOpenChange?.(false);

        (
          document.querySelector(
            '[aria-label="Select language"]'
          ) as HTMLElement | null
        )?.focus();

        return;
      }

      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIndex((i) => (i + 1) % LANGUAGES.length);
        return;
      }

      if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIndex((i) => (i - 1 + LANGUAGES.length) % LANGUAGES.length);
        return;
      }

      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        const lang = LANGUAGES[activeIndex];
        if (lang) changeLanguage(lang.code);
      }
    };

    return (
      <div className="relative" ref={wrapperRef}>
        {/* Icon Button */}
        {!hideTrigger && (
          <div
            role="button"
            tabIndex={0}
            onClick={() => {
              if (saving) return;

              setInternalOpen((v) => {
                const next = !v;
                onOpenChange?.(next);
                return next;
              });
            }}
            className={`inline-flex h-9 w-9 items-center justify-center rounded-md ${saving ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-100 cursor-pointer"
              }`}
            aria-haspopup="menu"
            aria-expanded={open}
            aria-label="Select language"
          >
            <Globe01 className="h-5 w-5 text-gray-700" />
          </div>
        )}

        {/* Dropdown */}
        {open && (
          <div
            ref={listRef}
            role="menu"
            tabIndex={-1}
            onKeyDown={onMenuKeyDown}
            className="absolute right-0 mt-2 w-48 rounded-xl bg-white border border-gray-200 shadow-lg z-[1100]"
          >
            <div className="py-1">
              {LANGUAGES.map((lang, idx) => {
                const active = currentLang === lang.code;

                return (
                  <div key={`${lang.code}-${idx}`}>
                    <button
                      ref={idx === 0 ? firstItemRef : null}
                      type="button"
                      role="menuitemradio"
                      aria-checked={active}
                      disabled={saving}
                      onMouseEnter={() => setActiveIndex(idx)}
                      onClick={() => changeLanguage(lang.code)}
                      className={`flex w-full items-center justify-between px-4 py-2 text-sm ${active ? "text-gray-950 font-medium" : "text-gray-700 hover:bg-gray-50"
                        } ${saving ? "opacity-50 cursor-not-allowed" : ""}`}
                    >
                      <span>{lang.label}</span>

                      {saving ? (
                        <span className="text-xs text-gray-400">...</span> // loading
                      ) : active ? (
                        <span className="text-orange-500 text-sm">✓</span>
                      ) : null}
                    </button>

                    {idx !== LANGUAGES.length - 1 && (
                      <div className="mx-4 border-t border-gray-200" />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };

export default LanguageSwitcher;
